import React, { forwardRef, useRef, useState } from 'react';

import { Input, type InputProps } from '@/components/ui/input';
import useClickOutside from '@/hooks/use-click-outside';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';

import { type SuggestionMapped, usePlaceSuggestions } from '../api/place-suggestions';

export type OnSelectSuggestion = (data: SuggestionMapped) => void;

interface AddressAutocompleteProps extends InputProps {
  onSelectSuggestion: OnSelectSuggestion;
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = forwardRef<
  HTMLInputElement,
  AddressAutocompleteProps
>(({ onSelectSuggestion, value, ...inputProps }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data } = usePlaceSuggestions(searchTerm);
  const suggestions = data ?? [];

  const prevSelectedValue = useRef('');

  const clearSuggestions = () => setSearchTerm('');

  const debouncedSetSearchTerm = useDebouncedCallback(setSearchTerm, 500);

  const containerRef = useClickOutside<HTMLDivElement>(clearSuggestions, !!suggestions.length);

  const handleSuggestionClick = (data: SuggestionMapped) => () => {
    prevSelectedValue.current = data.place_name;
    clearSuggestions();
    return onSelectSuggestion(data);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  React.useEffect(() => {
    if (value && value !== prevSelectedValue.current) {
      debouncedSetSearchTerm(value as string);
    }
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  React.useLayoutEffect(() => {
    if (value) {
      prevSelectedValue.current = value?.toString();
    }
  }, []);

  return (
    <div className="relative w-full">
      <Input {...inputProps} ref={ref} value={value} />
      {suggestions.length > 0 && (
        <div
          ref={containerRef}
          className="absolute z-50 mt-2 flex max-h-80 w-full flex-col gap-1 overflow-y-scroll rounded-md border border-gray-200 bg-white p-2 shadow-md"
        >
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              className="relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm border border-transparent px-2 py-1.5 text-md text-primary outline-none transition-colors hover:border-primary-light data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onClick={handleSuggestionClick(suggestion)}
            >
              {suggestion.place_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
